import { fetchWithHeaders, fileUtils } from '@sendible/common';
import { MediaIdType, MediaType } from '@sendible/shared-state-bridge';
import { completeUpload, uploadParts } from './utils';
import endpoints from '../../data-layer/endpoints';
import { MediaFileType } from '../medias/types';
import { getUrlWithCredentialsFromUser } from '../../utils';

export const useUploadFiles = ({ addToUploadingMedia, user, onProgress, onError }: UseUploadFilesParam) => {
  const isAbortError = (error: unknown) => (error as Error)?.name === 'AbortError';

  const uploadFile = async (
    file: File,
    fileId: string,
    onMediaCreated: (args: OnMediaCreatedFilesArgsType) => void,
    setMediaId: (id: MediaIdType) => void
  ) => {
    const { method: uploadMethod, endpoint: uploadEndpoint } = endpoints.UploadMedias;
    const { method: mediaMethod, endpoint: mediaEndpoint } = endpoints.CreateMedia;

    const body: MediaUploadBodyType = {
      source: {
        type: 'File',
        filename: file.name,
        size: file.size,
        contentType: file.type,
      },
    };

    body.initiator = 'ComposeBox';

    const uploadIntentResponse = (await fetchWithHeaders({
      method: uploadMethod,
      url: getUrlWithCredentialsFromUser(uploadEndpoint, user),
      body,
      params: { dashboardId: user.userId },
    })) as { result: MediaUploadIntentType };
    const { result: uploadIntent } = uploadIntentResponse;

    const mediaResponse = (await fetchWithHeaders({
      method: mediaMethod,
      url: getUrlWithCredentialsFromUser(mediaEndpoint, user),
      body: {
        name: uploadIntent.fileName,
        components: {
          original: uploadIntent.id,
        },
      },
      params: { dashboardId: user.userId },
    })) as { result: MediaType };
    const { result: media } = mediaResponse;

    setMediaId(media.id);

    const { cancel, uploadPromise } = uploadParts(uploadIntent, file, fileId, onProgress);

    addToUploadingMedia({ ...media, cancel });

    onMediaCreated({ file, fileId, mediaId: media.id, mediaType: file.type as MediaFileType });

    const uploadedParts = await uploadPromise;

    await completeUpload(uploadIntent, uploadedParts, user);

    return media.id;
  };

  return async ({
    files,
    onUploadStarted,
    onMediaCreated = () => null,
  }: {
    files: File[];
    onUploadStarted: (file: File) => string;
    onMediaCreated?: (args: OnMediaCreatedFilesArgsType) => void;
  }) => {
    if (files.length) {
      files.forEach(async (file) => {
        const fileName = file.name && !fileUtils.hasOnlyExtension(file.name) ? file.name : 'Attachment';
        const sanitizedFileName = fileUtils.sanitizeFileName(fileName, {
          replace: [
            {
              pattern: /[, ]/g,
              replacement: '_',
            },
          ],
        });

        const sanitizedFile = new File([file], sanitizedFileName, { type: file.type, lastModified: file.lastModified });
        const fileId = onUploadStarted(sanitizedFile);

        let createdMediaId: MediaIdType | undefined;

        try {
          await uploadFile(
            sanitizedFile,
            fileId,
            (args) => onMediaCreated({ ...args, file }),
            (id) => {
              createdMediaId = id;
            }
          );
        } catch (error) {
          if (isAbortError(error)) return;

          onError(error, { file: sanitizedFile, fileId, mediaId: createdMediaId });
        }
      });
    }
  };
};
